import React from "react";
import { Alert, Col, Row, TabPane } from "reactstrap";
import { PVR_FIELDS, FieldMapping, ImportResults, ImportStatus, MAX_IMPORT_RECORDS } from "./pvrImportFields";

export type PVRsImportWizardTabFinishProps = {
  csvRows: Record<string, string>[];
  fieldMappings: FieldMapping;
  importStatus: ImportStatus;
  importProgress: { current: number; total: number };
  importResults: ImportResults | null;
  importErrors: string[];
  onRunImport: () => void;
  onStartOver: () => void;
};

const PVRsImportWizardTabFinish: React.FC<PVRsImportWizardTabFinishProps> = ({
  csvRows, fieldMappings, importStatus, importProgress, importResults, importErrors, onRunImport, onStartOver,
}) => {
  const progressPct = importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0;
  const mappedFields = PVR_FIELDS.filter((f) => !!fieldMappings[f.key]);
  const effectiveRows = Math.min(csvRows.length, MAX_IMPORT_RECORDS);
  const isCapped = csvRows.length > MAX_IMPORT_RECORDS;

  return (
    <TabPane tabId={4}>
      {importStatus === "idle" && (
        <>
          <div className="d-flex align-items-start gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid #eef0f4" }}>
            <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, background: "#eef2ff" }}>
              <i className="ri-upload-2-line" style={{ fontSize: 18, color: "#4361ee" }} />
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-0 fw-semibold">Ready to Import</h5>
              <p className="text-muted mb-0 mt-1">Review the summary below, then click <strong>Start Import</strong> to begin.</p>
            </div>
            <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">Step 4 of 4</span>
          </div>

          {isCapped && (
            <div className="d-flex align-items-start gap-2 rounded-2 mb-4 px-3 py-3" style={{ background: "#fffbeb", border: "1px solid #fcd34d" }}>
              <i className="ri-error-warning-line flex-shrink-0 mt-1" style={{ color: "#d97706" }} />
              <span style={{ color: "#92400e" }}>
                Your file contains <strong>{csvRows.length.toLocaleString()}</strong> rows. Only the first <strong>{MAX_IMPORT_RECORDS.toLocaleString()}</strong> records will be imported per request. Please split your file to import the remaining <strong>{(csvRows.length - MAX_IMPORT_RECORDS).toLocaleString()}</strong> rows.
              </span>
            </div>
          )}

          <Row className="g-3 mb-4">
            <Col sm={6} md={4}>
              <div className="rounded-3 p-3 h-100" style={{ background: "#eef2ff", border: "1px solid #c7d2fe" }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="ri-file-list-line" style={{ color: "#4361ee", fontSize: 18 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#4361ee", textTransform: "uppercase", letterSpacing: 0.5 }}>Rows to Import</span>
                </div>
                <div className="d-flex align-items-baseline gap-2">
                  <span className="fw-bold" style={{ fontSize: 28, color: "#1e293b", lineHeight: 1 }}>{effectiveRows.toLocaleString()}</span>
                  {isCapped && <span className="badge rounded-pill" style={{ background: "#fcd34d", color: "#92400e", fontSize: 11, fontWeight: 600 }}>capped</span>}
                </div>
                {isCapped && <div className="mt-1" style={{ fontSize: 13, color: "#64748b" }}>of {csvRows.length.toLocaleString()} total rows</div>}
              </div>
            </Col>
            <Col sm={6} md={4}>
              <div className="rounded-3 p-3 h-100" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="ri-git-merge-line" style={{ color: "#16a34a", fontSize: 18 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#16a34a", textTransform: "uppercase", letterSpacing: 0.5 }}>Fields Mapped</span>
                </div>
                <div className="d-flex align-items-baseline gap-2">
                  <span className="fw-bold" style={{ fontSize: 28, color: "#1e293b", lineHeight: 1 }}>{mappedFields.length}</span>
                  <span style={{ color: "#64748b" }}>of {PVR_FIELDS.length}</span>
                </div>
              </div>
            </Col>
            <Col sm={12} md={4}>
              <div className="rounded-3 p-3 h-100" style={{ background: "#faf5ff", border: "1px solid #e9d5ff" }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="ri-shield-check-line" style={{ color: "#7c3aed", fontSize: 18 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 0.5 }}>Required Fields</span>
                </div>
                <div style={{ color: "#1e293b", fontWeight: 500 }}>
                  {PVR_FIELDS.filter((f) => f.required && fieldMappings[f.key]).length} of {PVR_FIELDS.filter((f) => f.required).length} required fields mapped
                </div>
                <div className="mt-1" style={{ fontSize: 13, color: "#64748b" }}>All required fields are ready</div>
              </div>
            </Col>
          </Row>

          <div className="d-flex align-items-center gap-3 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
            <button type="button" className="btn btn-light btn-label" onClick={onStartOver}>
              <i className="ri-restart-line label-icon align-middle fs-16 me-2" />Start Over
            </button>
            <button type="button" className="btn btn-success btn-label right ms-auto" onClick={onRunImport} disabled={csvRows.length === 0}>
              Start Import<i className="ri-upload-2-line label-icon align-middle fs-16 ms-2" />
            </button>
          </div>
        </>
      )}

      {importStatus === "running" && (
        <div className="text-center py-5">
          <div className="d-flex align-items-center justify-content-center mb-4">
            <div className="position-relative d-flex align-items-center justify-content-center" style={{ width: 80, height: 80 }}>
              <div className="spinner-border text-primary position-absolute" style={{ width: 80, height: 80, borderWidth: 4 }} role="status"><span className="visually-hidden">Importing…</span></div>
              <i className="ri-upload-2-line text-primary" style={{ fontSize: 28 }} />
            </div>
          </div>
          <h5 className="fw-semibold mb-1">Importing…</h5>
          <p className="text-muted mb-4" style={{ maxWidth: 380, margin: "0 auto 24px" }}>Please wait while your data is being processed. This may take a moment for larger files.</p>
          <div className="mx-auto" style={{ maxWidth: 460 }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ color: "#64748b" }}>Progress</span>
              <span className="fw-semibold" style={{ color: "#4361ee" }}>{progressPct}%</span>
            </div>
            <div className="progress" style={{ height: 8, borderRadius: 99, background: "#e2e8f0" }}>
              <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                style={{ width: `${progressPct}%`, background: "linear-gradient(90deg, #4361ee, #6c8aff)", borderRadius: 99, transition: "width 0.4s ease" }}
                aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100} />
            </div>
          </div>
        </div>
      )}

      {importStatus === "done" && importResults && (
        <div className="py-4">
          <div className="text-center mb-4">
            <div className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle" style={{ width: 72, height: 72, background: importResults.failed === 0 ? "#dcfce7" : "#fff7ed" }}>
              <i className={importResults.failed === 0 ? "ri-checkbox-circle-line" : "ri-alert-line"} style={{ fontSize: 36, color: importResults.failed === 0 ? "#16a34a" : "#ea580c" }} />
            </div>
            <h5 className="fw-semibold mb-1">{importResults.failed === 0 ? "Import Complete" : "Import Finished with Errors"}</h5>
            <p className="text-muted mb-0">{importResults.total.toLocaleString()} records were processed.</p>
          </div>
          <Row className="g-3 justify-content-center mb-4">
            <Col xs="auto">
              <div className="rounded-3 px-4 py-3 text-center" style={{ minWidth: 150, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "#16a34a", lineHeight: 1 }}>{importResults.successful.toLocaleString()}</div>
                <div className="d-flex align-items-center justify-content-center gap-1 mt-2" style={{ color: "#64748b" }}><i className="ri-check-line text-success" />Imported successfully</div>
              </div>
            </Col>
            {importResults.failed > 0 && (
              <Col xs="auto">
                <div className="rounded-3 px-4 py-3 text-center" style={{ minWidth: 150, background: "#fff1f2", border: "1px solid #fecdd3" }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: "#e11d48", lineHeight: 1 }}>{importResults.failed.toLocaleString()}</div>
                  <div className="d-flex align-items-center justify-content-center gap-1 mt-2" style={{ color: "#64748b" }}><i className="ri-close-line text-danger" />Failed to import</div>
                </div>
              </Col>
            )}
          </Row>
          {importResults.failed > 0 && importResults.downloadUrl && (
            <div className="d-flex align-items-center gap-3 rounded-2 px-4 py-3 mb-4 mx-auto" style={{ maxWidth: 480, background: "#fff7ed", border: "1px solid #fed7aa" }}>
              <i className="ri-file-download-line flex-shrink-0" style={{ fontSize: 22, color: "#ea580c" }} />
              <div className="flex-grow-1">
                <div className="fw-semibold" style={{ color: "#9a3412" }}>{importResults.failed.toLocaleString()} record{importResults.failed !== 1 ? "s" : ""} could not be imported</div>
                <div className="mt-1" style={{ fontSize: 13, color: "#c2410c" }}>Download the failed records CSV to review and retry.</div>
              </div>
              <a href={importResults.downloadUrl} download="failed-records.csv" className="btn btn-sm btn-outline-danger flex-shrink-0">
                <i className="ri-upload-2-line me-1" />Download
              </a>
            </div>
          )}
          {importResults.failed > 0 && !importResults.downloadUrl && (
            <p className="text-center text-muted fs-13 mb-4"><i className="ri-error-warning-line me-1 text-warning" />{importResults.failed.toLocaleString()} record{importResults.failed !== 1 ? "s" : ""} could not be imported.</p>
          )}
          {importErrors.length > 0 && (
            <Alert color="danger" className="text-start mb-4" style={{ maxWidth: 560, margin: "0 auto 1rem" }}>
              <div className="fw-semibold mb-1 d-flex align-items-center gap-2"><i className="ri-error-warning-line" />Errors encountered</div>
              <ul className="mb-0 mt-2 ps-3">{Array.from(new Set(importErrors)).map((e, i) => <li key={i}>{e}</li>)}</ul>
            </Alert>
          )}
          <div className="text-center pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
            <button type="button" className="btn btn-light btn-label" onClick={onStartOver}><i className="ri-restart-line label-icon align-middle fs-16 me-2" />Start Over</button>
          </div>
        </div>
      )}
    </TabPane>
  );
};

export default PVRsImportWizardTabFinish;
